import React, { useState } from 'react';
import Button from './Button';
import { MODELS, ADMIN_USER_EMAILS, DEFAULT_MODEL_ID } from '../constants';

interface MarkingModelsSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedModelIds: string[]) => void;
  title?: string;
  description?: string;
  userEmail?: string;
}

const MarkingModelsSelectionModal: React.FC<MarkingModelsSelectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Select AI Models for Essay Marking",
  description = "Choose which AI models you want to use for marking this essay. You can select multiple models for comparison.",
  userEmail
}) => {
  // Check if user is admin
  const isAdmin = userEmail && ADMIN_USER_EMAILS.includes(userEmail);
  
  // Filter models based on admin status
  const availableModels = isAdmin 
    ? Object.values(MODELS)
    : Object.values(MODELS).filter(model => model.id === DEFAULT_MODEL_ID);
  
  // Set default selection based on admin status
  const defaultSelection = [DEFAULT_MODEL_ID];
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>(defaultSelection);
  const [error, setError] = useState<string>('');

  const handleModelToggle = (modelId: string) => {
    const newSelection = selectedModelIds.includes(modelId)
      ? selectedModelIds.filter(id => id !== modelId)
      : [...selectedModelIds, modelId];
    
    setSelectedModelIds(newSelection);
    if (newSelection.length > 0) {
      setError('');
    }
  };

  const handleConfirm = () => {
    if (selectedModelIds.length === 0) {
      setError('Please select at least one AI model for marking.');
      return;
    }
    onConfirm(selectedModelIds);
    onClose();
  };

  const handleClose = () => {
    setSelectedModelIds(defaultSelection);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {title}
          </h2>
          <p className="text-gray-600">
            {description}
          </p>
        </div>

        <div className="p-6 overflow-y-auto max-h-96">
          {!isAdmin && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h3 className="text-sm font-medium text-blue-800">Standard AI model</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    You'll be using the standard AI configuration chosen by the team for consistent, high-quality essay marking.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {availableModels.map((model) => (
              <div key={model.id} className="relative">
                <label className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                  selectedModelIds.includes(model.id)
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
                }`}>
                  <input
                    type="checkbox"
                    checked={selectedModelIds.includes(model.id)}
                    onChange={() => handleModelToggle(model.id)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 mt-1 flex-shrink-0"
                  />
                  <div className="ml-3 min-w-0">
                    <div className="font-medium text-gray-900">{model.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{model.description}</div>
                  </div>
                </label>
              </div>
            ))}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {selectedModelIds.length > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Selected models:</strong> {selectedModelIds.map(id => MODELS[id].name).join(', ')}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                The essay will be marked by {selectedModelIds.length} selected model{selectedModelIds.length !== 1 ? 's' : ''}.
              </p>
            </div>
          )}

          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start">
              <svg className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-green-800">Model Selection</h3>
                <p className="text-sm text-green-700 mt-1">
                  You can choose multiple models to compare their marking approaches. Each model will provide independent scores and feedback.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={handleClose}
            variant="secondary"
            className="order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="primary"
            disabled={selectedModelIds.length === 0}
            className="order-1 sm:order-2"
          >
            Start Marking ({selectedModelIds.length} model{selectedModelIds.length !== 1 ? 's' : ''})
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarkingModelsSelectionModal;
